/**
 * useBoard — fetches `/api/boards/:projectId` for the read-only Phase 2a
 * board. Returns `{ project, statuses, cards, members }` in one round-trip
 * so the page renders a single render pass.
 *
 * Disabled when `projectId` is empty/undefined — the route param is
 * required, but `useBoard('')` is the cleanest call shape during
 * navigation transitions.
 *
 * ## `includeHidden` — for LOOKUP callers, not the board
 *
 * By default the BE hides cards that have sat in `Done` past the
 * project's visibility window. That's a BOARD-RENDERING rule: it stops a
 * Done column accumulating finished work forever. It is not an access
 * or permission rule — a hidden card is fully alive.
 *
 * This payload is the handiest "every card on this project" source in
 * the app, so non-board surfaces reach for it: pickers, autocompletes,
 * and id→title resolvers. **Every one of those must pass
 * `{ includeHidden: true }`.** Without it a card silently becomes
 * un-embeddable, un-linkable, or renders as a bare id once it ages out
 * of Done — the wiki work-item picker shipped with exactly that bug.
 *
 * The opt-out gets its own cache entry (`['board', id, 'all']`) so a
 * picker's unfiltered read can never leak into the board's rendering.
 * Every existing `invalidateQueries({ queryKey: BOARD_QUERY_KEY(id) })`
 * still reaches it — TanStack Query matches query keys by prefix — so
 * mutations refresh both variants with no extra call. The reverse is
 * not true of the optimistic `setQueryData` writers, which target the
 * board key exactly and leave the lookup variant to its refetch; that's
 * the intended split, since only the board surface needs the
 * zero-latency DnD update.
 */

import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { BoardResponse } from '@/api/types-bridge'
import { seedWorkingAgents } from '@/features/agents/use-agent-working'

export const BOARD_QUERY_KEY = (projectId: string) => ['board', projectId] as const

/**
 * Cache key for the hidden-inclusive read. A distinct entry rather
 * than a shared one: the two responses genuinely differ, and a picker
 * priming the board's cache with hidden cards would silently undo the
 * feature on the next board render.
 */
export const BOARD_ALL_CARDS_QUERY_KEY = (projectId: string) => ['board', projectId, 'all'] as const

export interface UseBoardOptions {
  /**
   * Include cards hidden by the Done visibility window. Set this on any
   * lookup / browse / picker surface; leave it off to render the board.
   */
  includeHidden?: boolean
}

export function useBoard(projectId: string | undefined, options: UseBoardOptions = {}) {
  const includeHidden = options.includeHidden === true
  return useQuery({
    queryKey: includeHidden
      ? BOARD_ALL_CARDS_QUERY_KEY(projectId ?? '')
      : BOARD_QUERY_KEY(projectId ?? ''),
    queryFn: async () => {
      // BE wraps every response in `{ data: ... }` (Phase 2b Pass 1).
      // Unwrap here so consumers keep using `board.data.project` etc.
      const requestedAt = Date.now()
      const res = await apiClient<{ data: BoardResponse }>(
        `/api/boards/${projectId}${includeHidden ? '?includeHidden=1' : ''}`,
      )
      // Seeds the "agent is working on this" glow, so a tab opened mid-run
      // already shows it.
      seedWorkingAgents(res.data.cards, requestedAt)
      return res.data
    },
    enabled: Boolean(projectId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}
